import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { MetricsService } from '../../../services/api/metrics.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FullMetricInfo, MetricInfo } from '../../../models/metrics/metric-info';
import { combineLatest } from 'rxjs';
import { translate } from '@jsverse/transloco';
import { TicketingService } from '../../../services/api/ticketing.service';
import { InstancesService } from '../../../services/api/instances.service';
import { Ticket } from '../../../models/tickets/ticket';
import { DateRange } from '@angular/material/datepicker';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { DataZone } from 'src/app/models/data-zone/data-zone';

@Component({
    selector: 'app-enterprise-dashboard',
    templateUrl: './enterprise-dashboard.component.html',
    styleUrls: ['./enterprise-dashboard.component.scss'],
    standalone: false
})
export class EnterpriseDashboardComponent  implements OnInit {

  fullMetricInfos: FullMetricInfo[] = [];
  tickets: Ticket[] = [];
  recentTickets: Ticket[] = [];
  showPlaceHolder = false;
  private destroyRef = inject(DestroyRef);

  constructor(private meteringService: MetricsService,
              private ticketingService: TicketingService,
              private instancesService: InstancesService) {
  }

  ngOnInit(): void {
    this.loadInstances();
    this.loadTickets();
  }

  private loadInstances(): void {
    this.instancesService.getInstances()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(instances => {
        const liveInstances = instances.filter(instance => instance.status === DeployStatus.DEPLOYED
          && instance.dataZone === DataZone.LIVE);
        if (liveInstances && liveInstances.length > 0) {
          this.loadMetricData(liveInstances.map(instance => instance.id));
        } else {
          this.fullMetricInfos = this.loadPlaceholderMetrics();
          this.showPlaceHolder = true;
        }
      });
  }

  private loadPlaceholderMetrics(): FullMetricInfo []  {
    return [
      {
        title: translate('PERFORMANCE_METRICS.SYSTEM_HEALTH'),
        label: '',
        metricInfo: {
          text: translate('PERFORMANCE_METRICS.HEALTHY'),
          iconHref: '/assets/icons/SYSTEM_HEALTH.svg',
          hasError: false,
          percentage: 100
        } as MetricInfo
      },
      {
        title: translate('PERFORMANCE_METRICS.SYSTEM_SECURITY'),
        label: '',
        metricInfo: {
          text: translate('PERFORMANCE_METRICS.SECURE'),
          iconHref: '/assets/icons/SYSTEM_SECURITY.svg',
          hasError: false,
          percentage: 100
        } as MetricInfo
      },
      {
        title: translate('PERFORMANCE_METRICS.SERVICE_AVAILABILITY'),
        label: '',
        metricInfo: {
          text: '100%',
          iconHref: '/assets/icons/SERVICE_AVAILABILITY.svg',
          hasError: false,
          percentage: 100
        } as MetricInfo
      }
    ];
  }

  private loadMetricData(instanceIDs: string[]): void {
    combineLatest([
      this.meteringService.getSystemHealth(),
      this.ticketingService.getSecurityTickets(this.getOneMonthDateRange()),
      this.meteringService.getServiceAvailability(this.buildServiceAvailabilityParams(instanceIDs))
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([systemHealth, systemSecurity, serviceAvailability]) => {
        this.fullMetricInfos.push(this.getSystemHealthInfo(systemHealth));
        this.fullMetricInfos.push(this.getSystemSecurityInfo(systemSecurity));
        this.fullMetricInfos.push(this.getServiceAvailabilityInfo(serviceAvailability));
      }, () => {
        this.fullMetricInfos = this.loadPlaceholderMetrics();
        this.showPlaceHolder = true;
      });
  }

  private getOneMonthDateRange(): DateRange<Date> {
    const endAt = new Date();
    const startAt = new Date();
    startAt.setDate(startAt.getDate() - 30);
    return new DateRange<Date>(startAt, endAt);
  }

  private buildServiceAvailabilityParams(instanceIDs: string[]): any {
    const oneMonthRange = this.getOneMonthDateRange();
    return {
      startAt: oneMonthRange.start.toISOString(),
      endAt: oneMonthRange.end.toISOString(),
      resolution: 'M',
      instanceIDs,
    };
  }

  private getSystemHealthInfo(systemHealth): FullMetricInfo {
    const servicesDown = systemHealth.data.filter(d => !d.healthy).length;
    let label = 'NO_SERVICES_DOWN';

    if (servicesDown === 1) {
      label = 'SERVICE_DOWN';
    }

    if (servicesDown > 1) {
      label = 'SERVICES_DOWN';
    }

    const totalServices = systemHealth.data.length;
    const percentage = 100 - Math.round(servicesDown * 100 / totalServices);
    const text = translate(`PERFORMANCE_METRICS.${servicesDown > 0 ? 'ISSUES' : 'HEALTHY'}`);

    return {
      title: translate('PERFORMANCE_METRICS.SYSTEM_HEALTH'),
      label: translate(`PERFORMANCE_METRICS.${label}`, {servicesDown}),
      metricInfo: {
        percentage,
        text,
        iconHref: '/assets/icons/SYSTEM_HEALTH.svg',
        hasError: servicesDown > 0,
      } as MetricInfo
    } as FullMetricInfo;

  }

  private getSystemSecurityInfo(systemSecurity): FullMetricInfo {
    const tickets = systemSecurity.tickets;
    const secured = tickets.length === 0;
    const percentage = 100 - tickets.length;
    const text = translate(`PERFORMANCE_METRICS.${secured ? 'SECURE' : 'INSECURE'}`);

    let label = 'NO_SECURITY_THREATS';

    if (tickets.length === 1) {
      label = 'SECURITY_THREAT';
    }

    if (tickets.length > 1) {
      label = 'SECURITY_THREATS';
    }

    return {
      title: translate('PERFORMANCE_METRICS.SYSTEM_SECURITY'),
      label: translate(`PERFORMANCE_METRICS.${label}`, {securityThreats: tickets.length}),
      metricInfo: {
        percentage,
        text,
        iconHref: '/assets/icons/SYSTEM_SECURITY.svg',
        hasError: !secured,
      } as MetricInfo
    } as FullMetricInfo;
  }

  private getServiceAvailabilityInfo(serviceAvailability): FullMetricInfo {
    const data = serviceAvailability.information[0].data;
    let percentage = 0;
    data.forEach(availability => percentage += availability);
    percentage = Math.round(percentage / data.length);

    const text = `${percentage}%`;
    return {
      title: translate('PERFORMANCE_METRICS.SERVICE_AVAILABILITY'),
      label: translate('PERFORMANCE_METRICS.THIS_MONTH'),
      metricInfo: {
        percentage,
        text,
        iconHref: '/assets/icons/SERVICE_AVAILABILITY.svg',
        hasError: percentage < 90,
      } as MetricInfo
    } as FullMetricInfo;
  }

  private loadTickets(): void {
    this.ticketingService.getSupportTickets(this.getOneMonthDateRange())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(result => {
          this.tickets = result.tickets;
          this.recentTickets = this.tickets.slice(0, 3);
        });
  }
}
